import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faMagic,
  faRocket,
  faChartLine,
  faCalendarAlt,
  faFileAlt,
  faCheckCircle,
  faClock,
  faUsers,
  faBullhorn,
  faHandshake,
  faDollarSign,
  faGamepad,
  faLightbulb,
  faTimes
} from '@fortawesome/free-solid-svg-icons';
import Header from '../../component/layout/header';
import Footer from '../../component/layout/footer';
import Navigate from '../../../../util/Navigate';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import IndieSubmenu from '../../component/section/publishers/IndieSubmenu';

function PublisherMarketingPlanPage() {
  const currentUrl = typeof window !== 'undefined' ? window.location.href : '';
  // State to manage modal content (src) and type ('image' or 'pdf')
  const [modalContent, setModalContent] = useState({ src: null, type: null });

  const openModal = (src, type) => setModalContent({ src, type });
  const closeModal = () => setModalContent({ src: null, type: null });

  const planFeatures = [
    {
      icon: faChartLine,
      title: "Data-Driven Strategy",
      description: "Plans based on real performance data from thousands of game launches"
    },
    {
      icon: faCalendarAlt,
      title: "Custom Timeline",
      description: "Detailed month-by-month schedule tailored to your release date"
    },
    {
      icon: faDollarSign,
      title: "Budget Allocation",
      description: "Smart recommendations for spending based on your available budget"
    },
    {
      icon: faUsers,
      title: "Audience Targeting",
      description: "Identifies your ideal players and how to reach them effectively"
    },
    {
      icon: faBullhorn,
      title: "Channel Strategy",
      description: "Specific tactics for social, influencers, ads, PR and more"
    },
    {
      icon: faGamepad,
      title: "Platform Optimization",
      description: "Specialized approaches for Steam, consoles, mobile, etc."
    }
  ];

  const steps = [
    {
      number: 1,
      icon: faGamepad,
      title: "Tell Us About Your Game",
      description: "Basic details about your game, platforms, and release timeline"
    },
    {
      number: 2,
      icon: faLightbulb,
      title: "Define Your Goals",
      description: "Select your priorities like wishlists, sales, or player retention"
    },
    {
      number: 3,
      icon: faMagic,
      title: "Get Your Custom Plan",
      description: "Receive a comprehensive, actionable marketing strategy in minutes"
    }
  ];

  return (
    <>
      <Helmet prioritizeSeoTags>
        <title>Free Custom Marketing Plan for Your Game | Glitch Gaming</title>
        <meta name="description" content="Get a complete, data-driven marketing plan tailored to your game - covering social media, influencers, ads, PR and more. Created in minutes, completely free." />
        <meta property="og:image" content="https://www.glitch.fun/assets/images/publishers/marketing-plan-og.jpg" />
      </Helmet>
      <Header />

      {/* Modal for both PDF and Image */}
      {modalContent.src && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content-wrapper" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close-button" onClick={closeModal}>×</button>
            {modalContent.type === 'image' && (
              <img src={modalContent.src} alt="Enlarged preview" className="enlarged-image" />
            )}
            {modalContent.type === 'pdf' && (
              <iframe 
                src={modalContent.src} 
                title="Sample Plan PDF" 
                className="pdf-viewer"
              ></iframe>
            )}
          </div>
        </div>
      )}

      {/* Hero Section */}
      <section className="marketing-plan-hero">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-6 text-center text-lg-start">
              <h1 className="hero-title pt-5">
                <span className="gradient-text">Free Custom Marketing Plan</span><br />
                For Your Game
              </h1>
              <p className="hero-subtitle">Get a complete, data-driven marketing strategy tailored to your game - created in minutes, completely free</p>
              <div className="cta-group">
                <Link to={Navigate.publishersOnboardingMultistepPage()} className="btn btn-primary btn-lg me-3">
                  <FontAwesomeIcon icon={faRocket} className="me-2" />
                  Create Your Plan
                </Link>
                {/* This button now opens the PDF modal */}
                <button onClick={() => openModal('/assets/docs/Example-Go-To-Market-Strategy-Proposal.pdf', 'pdf')} className="btn btn-outline-light btn-lg">
                  See Sample Plan
                </button>
              </div>
              <div className="trust-badges mt-4" style={{ justifyContent: 'start' }}>
                <div className="badge-item">
                  <FontAwesomeIcon icon={faClock} className="me-2" />
                  Takes 5 Minutes
                </div>
                <div className="badge-item">
                  <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
                  No Credit Card Needed
                </div>
              </div>
            </div>
            <div className="col-lg-6 hero-visual pt-5">
              <img src="/assets/images/publishers/marketing-plan-hero.png"
                alt="Marketing Plan Preview"
                className="hero-image mt-5"
                onClick={() => openModal('/assets/images/publishers/marketing-plan-hero.png', 'image')} />
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="how-it-works py-5 bg-light">
        <div className="container">
          <h2 className="section-title text-center mb-5 text-black">Create Your Plan in 3 Simple Steps</h2>
          <div className="timeline">
            <div className="timeline-progress"></div>
            <div className="row g-4">
              {steps.map((step, index) => (
                <div className="col-md-4" key={index}>
                  <div className="step-card">
                    <div className="step-number">{step.number}</div>
                    <FontAwesomeIcon icon={step.icon} className="step-icon" />
                    <h4 className='text-black'>{step.title}</h4>
                    <p className='text-black'>{step.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="text-center mt-5">
            <Link to={Navigate.publishersOnboardingMultistepPage()} className="btn btn-primary btn-lg px-5">
              Start Your Plan Now
            </Link>
          </div>
        </div>
      </section>

      {/* Plan Features Section */}
      <section className="plan-features py-5">
        <div className="container">
          <h2 className="section-title text-center mb-5 text-black">What's Included In Your Marketing Plan</h2>
          <p className="lead text-center mb-5 text-black">Comprehensive coverage of all critical marketing areas</p>

          <div className="row g-4">
            {planFeatures.map((feature, index) => (
              <div className="col-md-4" key={index}>
                <div className="feature-card">
                  <FontAwesomeIcon icon={feature.icon} className="feature-icon" />
                  <h3 className='text-black'>{feature.title}</h3>
                  <p className='text-black'>{feature.description}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="plan-breakdown mt-5">
            <div className="row align-items-center">
              <div className="col-lg-6">
                <img src="/assets/images/publishers/marketing-plan-breakdown.png"
                  alt="Marketing Plan Breakdown"
                  className="img-fluid rounded-3"
                  onClick={() => openModal('/assets/images/publishers/marketing-plan-breakdown.png', 'image')} />
              </div>
              <div className="col-lg-6">
                <h3 className="mb-4 text-black">Detailed, Actionable Guidance</h3>
                <ul className="feature-list text-black">
                  <li>
                    <FontAwesomeIcon icon={faCheckCircle} className="text-success me-2" />
                    Month-by-month timeline with specific activities
                  </li>
                  <li>
                    <FontAwesomeIcon icon={faCheckCircle} className="text-success me-2" />
                    Budget allocation recommendations
                  </li>
                  <li>
                    <FontAwesomeIcon icon={faCheckCircle} className="text-success me-2" />
                    Channel-specific strategies (social, influencers, ads, etc.)
                  </li>
                  <li>
                    <FontAwesomeIcon icon={faCheckCircle} className="text-success me-2" />
                    Performance benchmarks and KPIs
                  </li>
                  <li>
                    <FontAwesomeIcon icon={faCheckCircle} className="text-success me-2" />
                    Platform-specific optimization (Steam, consoles, mobile)
                  </li>
                  <li>
                    <FontAwesomeIcon icon={faCheckCircle} className="text-success me-2" />
                    Downloadable PDF and HTML versions
                  </li>
                </ul>
                <button onClick={() => openModal('/assets/docs/Example-Go-To-Market-Strategy-Proposal.pdf', 'pdf')} className="btn btn-primary mt-3">
                  See Full Breakdown
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Comparison Section */}
      <section className="comparison py-5 bg-dark text-white">
        <div className="container">
          <h2 className="section-title text-center mb-5">Why Our Marketing Plans Work</h2>
          <div className="row">
            <div className="col-lg-6">
              <div className="comparison-card">
                <h3 className="text-danger">Generic Advice</h3>
                <ul className="comparison-list">
                  <li>
                    <FontAwesomeIcon icon={faTimes} className="me-2" />
                    One-size-fits-all approach
                  </li>
                  <li>
                    <FontAwesomeIcon icon={faTimes} className="me-2" />
                    No specific timelines
                  </li>
                  <li>
                    <FontAwesomeIcon icon={faTimes} className="me-2" />
                    Vague recommendations
                  </li>
                  <li>
                    <FontAwesomeIcon icon={faTimes} className="me-2" />
                    No budget guidance
                  </li>
                  <li>
                    <FontAwesomeIcon icon={faTimes} className="me-2" />
                    Not tailored to your game
                  </li>
                </ul>
              </div>
            </div>
            <div className="col-lg-6">
              <div className="comparison-card">
                <h3 className="text-success">Glitch Marketing Plan</h3>
                <ul className="comparison-list">
                  <li>
                    <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
                    Customized for your game
                  </li>
                  <li>
                    <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
                    Detailed month-by-month schedule
                  </li>
                  <li>
                    <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
                    Actionable, specific tactics
                  </li>
                  <li>
                    <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
                    Budget allocation recommendations
                  </li>
                  <li>
                    <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
                    Based on real launch data
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="final-cta py-5">
        <div className="container text-center">
          <h2 className="cta-title">Ready To Transform Your Game's Marketing?</h2>
          <p className="cta-subtitle">Join thousands of developers who launched successfully with our free marketing plans</p>
          <div className="cta-group">
            <Link to={Navigate.publishersOnboardingMultistepPage()} className="btn btn-primary btn-lg me-3">
              Create Free Plan
            </Link>
          </div>
          <div className="trust-badges mt-4">
            <div className="badge-item">
              <FontAwesomeIcon icon={faClock} className="me-2" />
              Takes Just 5 Minutes
            </div>
            <div className="badge-item">
              <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
              No Credit Card Required
            </div>
          </div>
        </div>
      </section>

      <IndieSubmenu />
      <Footer />

      <style jsx>{`
        .marketing-plan-hero {
          padding: 100px 0;
          background: linear-gradient(135deg, #1a1b2f 0%, #080e37 100%);
          color: white;
        }
        .hero-title {
          font-size: 2.8rem;
          margin-bottom: 1.5rem;
          line-height: 1.2;
        }
        .gradient-text {
          background: linear-gradient(90deg, #ff0052 0%, #9400ff 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
        }
        .hero-subtitle {
          font-size: 1.2rem;
          margin-bottom: 2rem;
          opacity: 0.9;
        }
        .hero-visual {
          position: relative;
          padding: 2rem;
        }
        .hero-image {
          width: 100%;
          border-radius: 20px;
          box-shadow: 0 25px 50px -12px rgba(0,0,0,0.25);
          transition: transform 0.3s ease;
        }
        .hero-image:hover {
          transform: translateY(-5px);
        }
        .trust-badges {
          display: flex;
          justify-content: center;
          gap: 1.5rem;
          flex-wrap: wrap;
        }
        .badge-item {
          display: flex;
          align-items: center;
          padding: 0.5rem 1rem;
          background: rgba(255,255,255,0.1);
          border-radius: 50px;
          font-size: 0.9rem;
        }
        .social-proof {
          background: #f8f9ff;
          padding: 4rem 0;
        }
        .testimonial-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 1.5rem;
        }
        .testimonial-card {
          background: white;
          padding: 1.5rem;
          border-radius: 12px;
          box-shadow: 0 5px 15px rgba(0,0,0,0.05);
        }
        .testimonial-content {
          font-style: italic;
          margin-bottom: 1rem;
          position: relative;
          padding-left: 1.5rem;
        }
        .testimonial-content:before {
          content: '"';
          position: absolute;
          left: 0;
          top: -0.5rem;
          font-size: 2rem;
          color: #ff0052;
          opacity: 0.3;
        }
        .testimonial-author {
          display: flex;
          align-items: center;
          gap: 1rem;
        }
        .testimonial-author img {
          width: 50px;
          height: 50px;
          border-radius: 50%;
          object-fit: cover;
        }
        .testimonial-author small {
          display: block;
          font-size: 0.8rem;
          opacity: 0.7;
        }
        .how-it-works {
          background: #f8f9ff;
        }
        .timeline {
          position: relative;
          padding: 2rem 0;
        }
        .timeline-progress {
          position: absolute;
          top: 50%;
          left: 0;
          right: 0;
          height: 4px;
          background: rgba(0,0,0,0.1);
          z-index: 1;
        }
        .step-card {
          position: relative;
          z-index: 2;
          background: white;
          padding: 2rem;
          border-radius: 12px;
          box-shadow: 0 10px 15px -3px rgba(0,0,0,0.1);
          text-align: center;
          transition: all 0.3s ease;
          height: 100%;
        }
        .step-card:hover {
          transform: translateY(-10px);
        }
        .step-card img {
          height: 180px;
          width: 100%;
          object-fit: contain;
          margin: 1rem 0;
          border-radius: 8px;
          cursor: zoom-in;
        }
        .step-number {
          width: 50px;
          height: 50px;
          background: #ff0052;
          color: white;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.5rem;
          margin: -40px auto 1rem;
        }
        .step-icon {
          font-size: 2rem;
          margin-bottom: 1rem;
          color: #ff0052;
        }
        .plan-features {
          padding: 4rem 0;
          background: white;
        }
        .feature-card {
          background: #f8f9ff;
          padding: 2rem;
          border-radius: 12px;
          text-align: center;
          height: 100%;
          transition: all 0.3s ease;
        }
        .feature-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 10px 20px rgba(0,0,0,0.1);
        }
        .feature-icon {
          font-size: 2.5rem;
          margin-bottom: 1rem;
          color: #ff0052;
        }
        .plan-breakdown {
          margin-top: 4rem;
        }
        .feature-list {
          list-style: none;
          padding-left: 0;
        }
        .feature-list li {
          margin-bottom: 1rem;
          padding-left: 2rem;
          position: relative;
        }
        .feature-list li:before {
          content: "";
          position: absolute;
          left: 0;
          top: 0.5rem;
          width: 1rem;
          height: 1rem;
          background-color: #ff0052;
          border-radius: 50%;
          opacity: 0.2;
        }
        .comparison {
          background: linear-gradient(135deg, #1a1b2f 0%, #080e37 100%);
        }
        .comparison-card {
          background: rgba(255,255,255,0.05);
          padding: 2rem;
          border-radius: 12px;
          height: 100%;
        }
        .comparison-list {
          list-style: none;
          padding-left: 0;
        }
        .comparison-list li {
          margin-bottom: 0.75rem;
          padding-left: 2rem;
          position: relative;
        }
        .comparison-list li:before {
          position: absolute;
          left: 0;
          top: 0.1rem;
        }
        .results {
          background: #f8f9ff;
        }
        .result-card {
          background: white;
          padding: 2rem;
          border-radius: 12px;
          text-align: center;
          height: 100%;
          box-shadow: 0 5px 15px rgba(0,0,0,0.05);
        }
        .result-value {
          font-size: 3rem;
          font-weight: bold;
          background: linear-gradient(90deg, #ff0052 0%, #9400ff 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          line-height: 1;
          margin-bottom: 0.5rem;
        }
        .result-label {
          font-size: 1.2rem;
          font-weight: bold;
          margin-bottom: 1rem;
        }
        .final-cta {
          background: linear-gradient(135deg, #ff0052 0%, #9400ff 100%);
          color: white;
          padding: 4rem 0;
          text-align: center;
        }
        .cta-title {
          font-size: 2.5rem;
          margin-bottom: 1rem;
        }
        .cta-subtitle {
          font-size: 1.2rem;
          margin-bottom: 2rem;
          opacity: 0.9;
        }

        /* Modal Styles */
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.9);
          display: flex;
          justify-content: center;
          align-items: center;
          z-index: 9999;
          padding: 20px;
          cursor: zoom-out;
        }

        .modal-content-wrapper {
          position: relative;
          cursor: default;
        }

        .modal-close-button {
          position: absolute;
          top: -15px;
          right: -15px;
          width: 35px;
          height: 35px;
          border-radius: 50%;
          background: white;
          color: black;
          border: 2px solid #333;
          font-size: 1.8rem;
          line-height: 1;
          cursor: pointer;
          z-index: 10000;
          display: flex;
          align-items: center;
          justify-content: center;
          padding-bottom: 4px;
        }

        .enlarged-image {
          max-width: 90vw;
          max-height: 90vh;
          object-fit: contain;
          border-radius: 8px;
          animation: zoomIn 0.3s ease;
        }
        
        .pdf-viewer {
          width: 85vw;
          height: 90vh;
          border: none;
          border-radius: 8px;
        }

        @keyframes zoomIn {
          from { transform: scale(0.95); opacity: 0; }
          to { transform: scale(1); opacity: 1; }
        }

        @media (max-width: 768px) {
          .hero-title {
            font-size: 2rem;
          }
          .hero-image {
            margin-top: 2rem;
          }
          .step-card {
            margin-bottom: 2rem;
          }
          .timeline-progress {
            display: none;
          }
          .enlarged-image {
            max-width: 95vw;
            max-height: 80vh;
          }
          .pdf-viewer {
            width: 95vw;
            height: 85vh;
          }
        }
      `}</style>
    </>
  );
}

export default PublisherMarketingPlanPage;